import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { addYears } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { getBuildingOptions } from '../Steps/data/adminManager.data'
import { selectUserData } from '../../../../../../reducers/userDataReducer'
import {
  getAmenities,
  selectAmenities,
} from '../../../../../../reducers/Organisation/reservationsReducer'
import {
  selectBuildings,
  fetchSingleBuilding,
} from '../../../../../../reducers/buildingSelectorReducer'
import {
  getBookTypesByBuildingId,
  selectBookTypes,
} from '../../../../../../reducers/Organisation/bookTypesReducer'
import {
  getClients,
  selectClients,
  selectStatusIsLoadedAll,
  setLoadingState,
} from '../../../../../../reducers/clientsReducer'
import { createGiftCard } from '../../../../../../reducers/rewardsReducer'
import { mapClientToUser, setMinMaxValue, getServicesAvailableDenominations } from '../helpers'
import {
  REWARDS_AVAILABLE_FLAG,
  getNamedHandler,
  toAmenityCardItem,
  toServiceCardItem,
  getGiftCardCategoryOptions,
} from '../../../helpers'
import BaseView from '../BaseView/BaseView'
import RewardsSelectRecipient from '../Steps/RewardsSelectRecipient'
import RewardsSelectCustomGiftCard from '../Steps/RewardsSelectCustomGiftCard'
import RewardsCustomCardSummary from '../Steps/RewardsCustomCardSummary'
import CustomCardItems from '../CustomCardItems/CustomCardItems'
import SelectField from '../../../../../FormLegacy/FormFieldsV2/SelectField'
import TextField from '../../../../../FormLegacy/FormFieldsV2/TextField'
import SelectDropdownInput from '../../../../../SelectDropdownInput/SelectDropdownInput'
import Icon from '../../../../../Icons'
import TextAreaField from '../../../../../FormLegacy/FormFieldsV2/TextAreaField'
import { TextInput } from '@/components/ui/text-input'
import { useSnackbar } from '../../../../../../helpers/hooks/useSnackbar'

const Custom = ({
  goBack = () => {},
  fetchSingleBuilding = () => {},
  userData,
  users,
  amenities,
  services,
  allClientsLoaded,
  getClients,
  getAmenities,
  getServices,
  type,
  selectedBuilding,
  buildings,
  closePopup,
  createGiftCard,
  setLoadingState,
  // Note: pre-selects and locks the recipient - used when sending a gift from a resident's own
  // profile, where the resident is implied by context and must not be changed. Locking the
  // resident also implies locking the building (no separate `lockBuilding` prop needed) - the
  // caller already scopes `selectedBuilding` to the resident's building in that case. It also
  // suppresses the post-send redirect to the standalone Gift Cards page below (see onSubmit) -
  // that redirect would otherwise navigate a Resident Profile user away from the profile - in
  // favor of calling `onSuccess`, which the caller uses to refetch its own already-visible table.
  lockResident = undefined,
  onSuccess = () => {},
}) => {
  const { t } = useTranslation(['rewards-gift', 'rewards-table', 'rewards-loyalty'])
  const navigate = useNavigate()
  const [clients, setClients] = useState(users)
  const [formState, setFormState] = useState({
    building: selectedBuilding ? selectedBuilding.id : null,
    resident: lockResident ?? null,
    card: null,
    cardValue: null,
    expDate: addYears(Date.now(), 1),
    note: null,
    message: null,
    sendSms: false,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [clearInput, setClearInput] = useState(() => () => {})
  const { displaySnackbar } = useSnackbar()

  const isPropertyManager = userData.user.role === 'PropertyManager'
  const isAdmin = userData.user.role === 'Administrator'

  const buildingData = isPropertyManager
    ? (userData.buildingIds || []).filter((building) => building[REWARDS_AVAILABLE_FLAG])
    : buildings || []

  const currentBuilding = buildingData.find((building) => building.id === formState.building)

  const buildingOptions = getBuildingOptions(buildingData) || []

  const handleFieldChange = ({ target: { name, value } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const handleBuildingChange = (e) => {
    handleSearchSelect(null, 'resident')
    setClients([])
    clearInput()
    handleFieldChange(e)
  }
  const handleSearchSelect = getNamedHandler(handleFieldChange)
  const handleCardSelect = getNamedHandler(handleFieldChange)

  const fetchClients = async (query = '', isScrolling = false, buildingId = formState.building) => {
    const limit = 15
    const offset =
      clients.length % limit === 0
        ? clients.length / limit
        : (clients.length - (clients.length % limit)) / limit

    await getClients(isScrolling ? offset + 1 : 1, buildingId, undefined, query, limit)
  }

  useEffect(() => {
    if (userData.user.id || formState.building) {
      fetchClients()
    }
  }, [userData.user.id, formState.building])

  useEffect(() => {
    setClients(users)
  }, [users])

  const fetchAmenities = async (buildingId) => {
    try {
      await getAmenities(buildingId)
    } catch ({ message }) {
      displaySnackbar(message, true)
    }
  }

  const fetchServices = async () => {
    try {
      await getServices(formState.building)
    } catch ({ message }) {
      displaySnackbar(message, true)
    }
  }

  useEffect(() => {
    if (formState.building) {
      switch (type) {
        case 'amenity':
          fetchAmenities(formState.building)
          break
        case 'services':
          fetchServices(formState.building)
          break
        default:
          break
      }
    }
  }, [formState.building, type])

  const onSubmitFinally = async () => {
    if (selectedBuilding.id) {
      await fetchSingleBuilding(selectedBuilding.id)
    }
    closePopup()
    setIsLoading(false)
  }

  const onSubmit = () => {
    const requestData = {
      amount: parseInt(formState.cardValue, 10),
      clientId: formState.resident.value,
      buildingId: formState.building,
      internalNote: formState.note,
      messageToRecipient: formState.message,
      ...(type === 'amenity'
        ? { type: 'amenity', amenityId: formState.card.id }
        : { type: 'service', serviceId: formState.card.id }),
      category: formState.category,
      sendSms: formState.sendSms,
    }

    setIsLoading(true)
    createGiftCard(requestData, 'custom')
      .then(() => {
        displaySnackbar(
          t('rewards-gift:giftCardSentSuccess', {
            cardName: formState.card.name,
            recipientLabel: formState.resident.label,
          }),
        )
        if (lockResident) {
          onSuccess()
        } else {
          navigate(`/dashboard/building-dashboard/rewards/gift-cards/${currentBuilding.id}`)
        }
      })
      .catch(() =>
        displaySnackbar(
          t('rewards-gift:giftCardCreateError', { cardName: formState.card.name }),
          true,
        ),
      )
      .finally(onSubmitFinally)
  }

  const selectRecipientButtonEnabled =
    !!formState.building && !!formState.resident && !!formState.cardValue && !!formState.category

  const selectCustomCardEnabled = !!formState.card

  const isService = type === 'services'

  const steps = [
    {
      Component: RewardsSelectRecipient,
      title: t('rewards-gift:sendGiftSteps.selectOptions'),
      config: {
        canGoNext: selectRecipientButtonEnabled,
        inputs: {
          // Note: locking the resident also locks the building (see `lockResident` note above) -
          // switching buildings would let the sender pick a different recipient's building.
          ...(!lockResident && {
            first: {
              render: () => (
                <SelectField
                  name="building"
                  options={buildingOptions}
                  placeholder={t('rewards-gift:sendGiftForm.selectBuildingPlaceholder')}
                  onChange={handleBuildingChange}
                  value={formState.building}
                  selectStyle={{ width: '100%' }}
                />
              ),
              label: t('rewards-gift:sendGiftForm.selectBuildingLabel'),
            },
          }),
          second: {
            render: () =>
              lockResident ? (
                <TextInput disabled readOnly value={lockResident.label} />
              ) : (
                <SelectDropdownInput
                  placeholder={t('rewards-gift:sendGiftForm.clickAndTypeSearchResidentPlaceholder')}
                  data={users}
                  onSelectOption={(item) => handleSearchSelect(item, 'resident')}
                  fetchData={(skip, query, isScrolling, buildingId) =>
                    fetchClients(query, isScrolling, buildingId)
                  }
                  isLoadedAll={allClientsLoaded}
                  isDisabled={formState.nonResident}
                  setLoadingState={setLoadingState}
                  dataToRefetch={formState.building}
                  showSelectedInDropdown
                  clearOnDemand={setClearInput}
                />
              ),
            label: t('rewards-gift:sendGiftForm.selectResidentLabel'),
          },
          third: {
            render: () =>
              isService ? (
                <SelectField
                  name="cardValue"
                  options={getServicesAvailableDenominations(t)}
                  placeholder={t('rewards-gift:sendGiftForm.selectPlaceholder')}
                  onChange={handleFieldChange}
                  value={formState.cardValue}
                  selectStyle={{ width: '100%' }}
                  additionalClass="services-value-select"
                />
              ) : (
                <TextField
                  name="cardValue"
                  onChange={handleFieldChange}
                  onBlur={(e) => handleFieldChange(setMinMaxValue(e))}
                  value={formState.cardValue || ''}
                  placeholder={t('rewards-gift:sendGiftForm.enterPlaceholder')}
                  style={{ width: '100%' }}
                  leftIcon={
                    <Icon
                      icon="dollar"
                      fill="none"
                      svgProps={{
                        stroke: '#3C3C3C',
                        strokeLinecap: 'round',
                        strokeLinejoin: 'round',
                      }}
                    />
                  }
                />
              ),
            label: t('rewards-gift:sendGiftForm.giftCardValueLabel'),
          },
          fourth: {
            render: () => (
              <SelectField
                name="category"
                options={getGiftCardCategoryOptions(t)}
                placeholder={t('rewards-gift:sendGiftForm.selectCardCategoryPlaceholder')}
                onChange={handleFieldChange}
                value={formState.category}
                selectStyle={{ width: '100%' }}
              />
            ),
            label: t('rewards-table:giftCardCategoryPlaceholder'),
          },
          fifth: {
            render: () => (
              <React.Fragment>
                <TextField
                  name="note"
                  onChange={handleFieldChange}
                  value={formState.note || ''}
                  placeholder={t('rewards-gift:sendGiftForm.enterInternalNotePlaceholder')}
                  style={{ width: '100%' }}
                />
                <span className="input-desc-label">
                  {t('rewards-gift:sendGiftForm.internalNoteDescription')}
                </span>
              </React.Fragment>
            ),
            label: (
              <div>
                {t('rewards-gift:sendGiftForm.internalNoteLabel')}{' '}
                <span className="input-desc-label">
                  ({t('rewards-loyalty:activityForm.optionalSuffix')})
                </span>
              </div>
            ),
          },
          sixth: {
            render: () => (
              <TextAreaField
                name="message"
                maxLength="260"
                onChange={handleFieldChange}
                value={formState.message || ''}
                placeholder={t('rewards-gift:sendGiftForm.messagePlaceholder')}
                style={{ width: '100%' }}
              />
            ),
            label: (
              <div>
                {t('rewards-gift:sendGiftForm.messageToRecipientLabel')}{' '}
                <span className="input-desc-label">
                  ({t('rewards-loyalty:activityForm.optionalSuffix')})
                </span>
              </div>
            ),
          },
        },
      },
    },
    {
      Component: RewardsSelectCustomGiftCard,
      title: t('rewards-gift:sendGiftSteps.selectGiftCard'),
      config: {
        canGoNext: selectCustomCardEnabled,
        inputs: {
          first: {
            render: () => (
              <CustomCardItems
                onCardSelect={(card) => handleCardSelect(card, 'card')}
                selectedCardId={formState.card ? formState.card.id : null}
                cards={isService ? services : amenities}
                isService={isService}
                isAdmin={isAdmin}
              />
            ),
          },
        },
      },
    },
    {
      Component: RewardsCustomCardSummary,
      title: t('rewards-gift:sendGiftSteps.summary'),
      config: {
        formState,
        isLoading,
        currentBalance: currentBuilding.rewardsBalance,
        isService,
        onSubmit,
        setFormState,
      },
    },
  ]

  return <BaseView steps={steps} goBack={goBack} />
}

Custom.propTypes = {
  goBack: PropTypes.func,
  userData: PropTypes.object,
  getClients: PropTypes.func,
  getAmenities: PropTypes.func,
  getServices: PropTypes.func,
  users: PropTypes.array,
  amenities: PropTypes.array,
  services: PropTypes.array,
  allClientsLoaded: PropTypes.bool,
  type: PropTypes.oneOf(['amenity', 'services']),
  selectedBuilding: PropTypes.object,
  buildings: PropTypes.array,
  closePopup: PropTypes.func,
  createGiftCard: PropTypes.func,
  setLoadingState: PropTypes.func,
  fetchSingleBuilding: PropTypes.func,
  lockResident: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.number,
    email: PropTypes.string,
    phone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onSuccess: PropTypes.func,
}

const mapStateToProps = (state, ownProps) => ({
  userData: selectUserData(state),
  buildings: selectBuildings(state),
  users: selectClients(state).map(mapClientToUser),
  allClientsLoaded: selectStatusIsLoadedAll(state),
  ...(ownProps.type === 'amenity'
    ? { amenities: selectAmenities(state).map(toAmenityCardItem) }
    : { services: selectBookTypes(state).map(toServiceCardItem) }),
})

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    createGiftCard: (...props) => dispatch(createGiftCard(...props)),
    getClients: (...props) => dispatch(getClients(...props)),
    setLoadingState: (...props) => dispatch(setLoadingState(...props)),
    fetchSingleBuilding: (...props) => dispatch(fetchSingleBuilding(...props)),
    ...(ownProps.type === 'amenity'
      ? { getAmenities: (...props) => dispatch(getAmenities(...props)) }
      : { getServices: (...props) => dispatch(getBookTypesByBuildingId(...props)) }),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Custom)
